import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LightboxModal from '../components/LightboxModal';
import { imageUrl } from '../lib/images';
import { weTransferBatches } from '../data/albumData';

export default function AlbumOversigtPage() {
  // Lightbox state
  const [lightboxState, setLightboxState] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0
  });

  const openLightbox = (imageList, index = 0) => {
    const formatted = imageList.map(img => ({
      src: img.sitePath || img.url,
      title: `${img.title || img.fileName} (${img.fileName})`,
      category: img.title || ''
    }));
    setLightboxState({
      isOpen: true,
      images: formatted,
      currentIndex: index
    });
  };

  return (
    <>
      <SEO 
        title="7 Billedalbums | Hovendals ApS"
        description="Oversigt over de 7 modtagne billedalbums med billeder og filnavne."
      />

      <section className="bg-slate-900 text-white py-12 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 bg-[#004FA4]/20 text-[#C2CCDF] text-xs font-semibold rounded-full uppercase tracking-wider mb-3">
            Oversigt
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-2">
            De 7 Billedalbums
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto text-base">
            Her er de 7 albums med tilhørende billeder og filnavne, så du nemt kan henvise til hvert albumnummer.
          </p>
        </div>
      </section>

      <section className="py-12 bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {weTransferBatches.map((batch, batchIndex) => (
              <div 
                key={batch.batchId} 
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
              >
                {/* Album Header */}
                <div className="bg-slate-900 text-white px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#004FA4] text-white font-bold text-lg shadow-md">
                      #{batchIndex + 1}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-white tracking-wide">
                          Album {batchIndex + 1}
                        </h2>
                        {batch.categoryName && (
                          <span className="text-xs bg-[#004FA4]/20 text-[#C2CCDF] font-semibold px-2.5 py-0.5 rounded-full border border-[#004FA4]/30">
                            {batch.categoryName}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {batch.images.length} {batch.images.length === 1 ? 'billede' : 'billeder'}
                        {batch.description && ` • ${batch.description}`}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {batch.targetPath && (
                      <Link
                        to={batch.targetPath}
                        className="text-xs font-semibold text-[#c2ccdf] hover:text-[#c2ccdf] bg-slate-800 hover:bg-slate-700/80 px-3 py-1.5 rounded-lg border border-slate-700 transition-colors inline-flex items-center gap-1.5"
                      >
                        <span>Se på siden</span>
                        <i className="fa-sharp fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
                      </Link>
                    )}
                    <span className="text-xs font-medium text-slate-400 bg-slate-800/80 px-3 py-1.5 rounded-lg border border-slate-700/50">
                      Album #{batchIndex + 1}
                    </span>
                  </div>
                </div>

                {/* Billeder Gitter */}
                <div className="p-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {batch.images.map((img, imgIndex) => (
                      <div 
                        key={img.fileName}
                        onClick={() => openLightbox(batch.images, imgIndex)}
                        className="group relative bg-slate-100 rounded-xl overflow-hidden border border-slate-200 hover:border-[#004fa4] hover:shadow-md transition-all cursor-pointer flex flex-col"
                      >
                        <div className="aspect-square w-full overflow-hidden bg-slate-200">
                          <img 
                            src={imageUrl(img.sitePath || img.url, 'width=400,quality=80,format=auto')} 
                            alt={img.fileName}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-2 text-center bg-white border-t border-slate-100 mt-auto">
                          <p className="text-[11px] font-mono font-medium text-slate-600 truncate" title={img.fileName}>
                            {img.fileName}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox til billedforstørrelse */}
      <LightboxModal 
        isOpen={lightboxState.isOpen}
        onClose={() => setLightboxState(prev => ({ ...prev, isOpen: false }))}
        images={lightboxState.images}
        currentIndex={lightboxState.currentIndex}
        onNavigate={(newIndex) => setLightboxState(prev => ({ ...prev, currentIndex: newIndex }))}
      />
    </>
  );
}
